import { http } from '@/utils/api'
import { withPbx } from '@/stores/network/api'
import * as Sentry from '@/core/sentry'
import type { OfficeDesk } from '@/stores/officeDesk/store'
import type { ColorName } from '@/utils/colors'
import { isWhitelistError } from '@/utils/api/whitelist'

export type Softkey = {
  id: number
  label: string
  type: string
  value: string
  order?: number
  provisioning_device_id?: number
}

export type Device = {
  blockOutgoing: boolean
  id: number
  isTrunkExisting: boolean
  isTrunkInPbxMode: boolean
  name: string
  type:
    | 'external'
    | 'dectBase'
    | 'voip'
    | 'app'
    | 'msTeams'
    | 'connectme'
    | 'aiAgent'
  groups: any[]
  status?: 'offline' | 'online' | 'error' | 'not_inuse' | 'inuse' | 'none'
  phoneNumber: string
  extensions: number[]
  externalReference: string | undefined
  username?: string
  color: ColorName | null
  dectBase: number | null
  primaryBase: number | null
  officeDesk?: OfficeDesk
  pbxId?: number
  createdAt?: string
  updatedAt?: string
  otherClipNdc?: string
  otherClipSn?: string
  manufacturer?: string
  outgoingTrunk?:
    | null
    | number
    | {
        id: number
        username: string
      }
  softkeys?: null | Softkey[]
  trustedIps?:
    | null
    | {
        created_at: string
        credentials_id: number
        id: number
        name: string
        type: string
        updated_at: string
        value: string
      }[]
  displayname?: string
  internalClip?: string
  clipType?: 'suppress' | 'custom' | 'extension'
  clips?:
    | []
    | {
        slot_id: number
        clip_ndc: string | null
        clip_sn: string | null
      }[]
  provisioning?: boolean
  provisioningUsername?: string
  provisioningMac?: string
  provisioningPassword?: string
  acCode?: string
  secureCalling?: boolean
  transmitContacts?: boolean
  differentRingtones?: boolean
  intercom?: boolean
  toneScheme?: string
  phoneLanguage?: string
  syncLevel?: number
  cluster?: number
  email?: string
  identifier?: string | null
  manufacturerSpecificId?: string
  provisioningAuth?: string
  provisioningTemplate?: number
  provisioningName?: string
  clipExtensionId?: number
  provisioningUrl?: string | null
  isUnlockPossible?: boolean | null
  appType?: 'mobile' | 'desktop' | 'web' | null
  password?: string
  qrCode?: string
  cloudUsername?: string
  cloudPassword?: string
  numericUserIdentifier?: number
  /** True if it is assigned to a CFC as static agent */
  assigned?: boolean
}

export async function loadDevice(id: string | number) {
  try {
    const url = withPbx(`devices/${id}`)

    const { data } = await http.get<Device>(url)

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function createDevice(inputData: any) {
  try {
    const url = withPbx('devices')

    const { data } = await http.post<Device>(url, inputData)

    return { status: 'success' as const, payload: data }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function updateDevice(id: string | number, inputData: any) {
  try {
    const url = withPbx(`devices/${id}`)

    const { data } = await http.put<Device>(url, inputData)

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function deleteDevice(id: string | number) {
  try {
    const url = withPbx(`devices/${id}`)

    const { data } = await http.delete(url)

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function updateSoftkeys(
  id: string | number,
  softkeys: Omit<Softkey, 'id'>[]
) {
  try {
    const url = withPbx(`devices/${id}/softkeys`)

    const { data } = await http.patch<Pick<Device, 'softkeys'>>(url, {
      softkeys,
    })

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function updateAllowedIps(
  id: string | number,
  allowedIps: Partial<NonNullable<Device['trustedIps']>[number]>[]
) {
  try {
    const url = withPbx(`devices/${id}/trusted-ips`)

    const { data } = await http.patch(url, { ips: allowedIps })

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}

export async function unlockProvisioning(id: number | string) {
  try {
    const url = withPbx(`devices/${id}/unlock-provisioning`)
    const { data } = await http.delete(url)

    return {
      status: 'success' as const,
      payload: data,
    }
  } catch (error) {
    if (!isWhitelistError(error)) {
      Sentry.captureException(error)
    }

    return {
      status: 'error' as const,
      payload: error,
    }
  }
}
